import React, { useEffect, useRef, useState } from 'react';
import { parseVideoUrl } from '../lib/video';

interface VideoEmbedProps {
  url: string;
  className?: string;
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
}

const VideoEmbed: React.FC<VideoEmbedProps> = ({
  url,
  className = '',
  autoplay = false,
  muted = false,
  controls = true,
}) => {
  const info = parseVideoUrl(url);
  const [directRatio, setDirectRatio] = useState<number | undefined>(info?.aspectRatio);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const updateRatio = () => {
      if (video.videoWidth && video.videoHeight) {
        setDirectRatio(video.videoWidth / video.videoHeight);
      }
    };
    video.addEventListener('loadedmetadata', updateRatio);
    if (video.readyState >= 1) updateRatio();
    return () => video.removeEventListener('loadedmetadata', updateRatio);
  }, [url]);

  if (!info) return null;

  const ratio = info.type === 'direct' ? directRatio : info.aspectRatio;
  const wrapperStyle: React.CSSProperties = ratio
    ? { aspectRatio: `${ratio}`, width: '100%' }
    : { width: '100%' };

  if (info.type === 'youtube' || info.type === 'vimeo') {
    return (
      <div style={wrapperStyle} className={`relative ${className}`}>
        <iframe
          src={`${info.embedUrl}${autoplay ? '&autoplay=1' : ''}${muted ? '&mute=1' : ''}`}
          title="product video"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={info.url}
      className={`w-full h-auto ${className}`}
      controls={controls}
      autoPlay={autoplay}
      muted={muted}
      playsInline
      preload="metadata"
      style={{ maxHeight: '90vh' }}
    />
  );
};

export default VideoEmbed;
